import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserBrief } from '../api/user';
import { getPresence } from '../api/presence';
import { createChat } from '../api/chats';
import { ApiError } from '../api/client';
import { formatPresence } from '../lib/presence';
import type { PresenceDto, UserBriefDto } from '../api/types';
import Avatar from './Avatar';
import EmojiText from './EmojiText';
import { backdropClose } from '../lib/modalBackdrop';

interface Props {
  userId: string;
  onClose: () => void;
  /** Открывает личный чат с этим участником (создаёт, если ещё не было) — см. кнопку "Написать". */
  onOpenChat: (chatId: string) => void;
}

/** Карточка участника группы (открывается из контекстного меню его сообщения) — облегчённая
 *  версия инфопанели чата: то же профильное представление, но без привязки к конкретному чату. */
export default function UserInfoModal({ userId, onClose, onOpenChat }: Props) {
  const { t } = useTranslation('people');
  const [user, setUser] = useState<UserBriefDto | null>(null);
  const [presence, setPresence] = useState<PresenceDto | null>(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');

  useEffect(() => {
    getUserBrief(userId)
      .then(setUser)
      .catch(() => setError(t('userInfo.profileLoadFailed')));
    getPresence(userId).then(setPresence).catch(() => {});
  }, [userId, t]);

  const startChat = async () => {
    setStarting(true);
    setStartError('');
    try {
      const chat = await createChat(1, null, [userId]);
      onOpenChat(chat.id);
      onClose();
    } catch (err) {
      setStartError(err instanceof ApiError ? err.message : t('userInfo.startChatFailed'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="modal-backdrop" {...backdropClose(onClose)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 340 }}>
        <button type="button" className="modal-close" title={t('userInfo.close')} onClick={onClose}>✕</button>
        {error && <div className="error">{error}</div>}
        {!user && !error && <div style={{ color: 'var(--muted)' }}>{t('userInfo.loading')}</div>}
        {user && (
          <>
            <div className="info-profile" style={{ padding: '4px 16px 16px' }}>
              <Avatar userId={user.id} name={user.displayName} size={88} version={user.avatarFileId} />
              <div className="info-profile-name">{user.displayName}</div>
              <div className="info-profile-status">{presence ? formatPresence(presence) : ' '}</div>
            </div>
            <div className="info-extra" style={{ marginBottom: 16 }}>
              {user.bio && (
                <div className="info-extra-row">
                  <span className="info-extra-subtitle">{t('userInfo.bio')}</span>
                  <span className="info-extra-title" style={{ whiteSpace: 'pre-wrap' }}>
                    <EmojiText text={user.bio} />
                  </span>
                </div>
              )}
              {user.readReceiptsDisabled && (
                <div className="info-extra-row">
                  <span className="info-extra-subtitle">{t('userInfo.privacy')}</span>
                  <span className="info-extra-title">{t('userInfo.readReceiptsDisabled')}</span>
                </div>
              )}
              <div className="info-extra-row">
                <span className="info-extra-subtitle">{t('userInfo.username')}</span>
                <span className="info-extra-title">@{user.username}</span>
              </div>
            </div>
            <button type="button" className="btn" style={{ width: '100%' }} disabled={starting} onClick={startChat}>
              {starting ? '…' : t('userInfo.write')}
            </button>
            {startError && <div className="error" style={{ marginTop: 8 }}>{startError}</div>}
          </>
        )}
      </div>
    </div>
  );
}
